import React, { useState } from "react";
import { useIntl } from "react-intl";

import { graphql, useMutation } from "react-relay";

import {
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "@/src/common/navigationHeaderOptions";
import { useAlert } from "@/src/common/Alert";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";

import { EmployeeRoleEditComponent } from "../EmployeeRoleEdit/EmployeeRoleEditComponent";

import { EmployeeRoleCreateMainMutation } from "./__generated__/EmployeeRoleCreateMainMutation.graphql";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorParamList,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";

type ParamList = {
  EmployeeRoleCreateMain: UserLoggedInNavigatorParamList["EmployeeRoleCreate"];
};

type EmployeeRoleCreateScreenProps<T extends keyof ParamList> = {
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"EmployeeRoleCreate">,
    NativeStackNavigationProp<ParamList, T>
  >;
  route: RouteProp<ParamList, T>;
};

const Stack = createNativeStackNavigator<ParamList>();

export const EmployeeRoleCreate = withWebModal(EmployeeRoleCreateC);

function EmployeeRoleCreateC(
  props: UserLoggedInNavigatorScreenProps<"EmployeeRoleCreate">
) {
  const intl_tokens = useIntlTokens();
  const getOpts = useNavigationHeaderOptions();
  const opts = getOpts({
    type: "CLOSE",
    title: intl_tokens.Role,
    onClose: () => {
      props.navigation.goBack();
    },
  });

  return (
    <Stack.Navigator initialRouteName="EmployeeRoleCreateMain">
      <Stack.Screen
        name="EmployeeRoleCreateMain"
        component={EmployeeRoleCreateMain}
        initialParams={props.route.params}
        options={{
          ...opts,
        }}
      />
    </Stack.Navigator>
  );
}

function EmployeeRoleCreateMain(
  props: EmployeeRoleCreateScreenProps<"EmployeeRoleCreateMain">
) {
  return <InnerEmployeeRoleCreate {...props} />;
}

function InnerEmployeeRoleCreate(
  props: EmployeeRoleCreateScreenProps<"EmployeeRoleCreateMain">
) {
  const { navigation, route } = props;
  const { teamId } = route.params;
  const intl_tokens = useIntlTokens();
  const intl = useIntl();
  const Alert = useAlert();

  const [name, setName] = useState("");
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);

  const [create, loading] = useMutation<EmployeeRoleCreateMainMutation>(graphql`
    mutation EmployeeRoleCreateMainMutation($input: CreateEmployeeRoleInput!) {
      store {
        createEmployeeRole(input: $input) {
          role {
            id
            name
            employeeCount
            team {
              id
              name
            }
          }
          store {
            id
          }
        }
      }
    }
  `);

  const onCreate = () => {
    create({
      variables: {
        input: {
          name,
          teamId,
          employeeIds,
        },
      },
      onCompleted: navigation.goBack,
      onError: () => {
        Alert.alert(
          intl.formatMessage({
            defaultMessage: "Failed to create role",
            description: "EmployeeRoleCreate: Error title",
          })
        );
      },
    });
  };

  useNavigationHeader({
    type: "CREATE",
    onCancel: navigation.goBack,
    onSave: onCreate,
    saveLoading: loading,
    disabled: name.length < 1,
    createButtonLabel: intl_tokens.Create,
    title: intl_tokens.Role,
    defaultContentStyle: true,
  });

  return (
    <EmployeeRoleEditComponent
      autoFocusName
      employeeIds={employeeIds}
      name={name}
      setName={setName}
      setEmployeeIds={setEmployeeIds}
      teamId={teamId}
    />
  );
}
